import type { FC, MouseEvent, ReactElement } from 'react'
import { useCallback } from 'react'
import { toast } from 'react-toastify'
import { useMyInfo } from 'src/services/apollo/auth'
import { useItemLiked, useItemLikedCount } from 'src/stores'
import { getErrorCode } from 'src/utils/errorUtil'
import { once } from 'src/utils/once'
import type { SignModalProps } from '../Account/types'
import { openSignModal } from '../Account/utils'
import Text from '../ui/Text'

const BaseLikeButton: FC<{
  id: string
  initialValue?: boolean
  initialCount?: number
  onSuccess?: (target: boolean) => any
  onBtnClick?: (target: boolean) => any
  handler: (id: string, target: boolean) => Promise<any>
  render: (props: {
    value: boolean
    count: number
    onClick: (e: MouseEvent) => void
  }) => ReactElement
  signModalProps?: SignModalProps
}> = ({
  id,
  initialCount,
  initialValue,
  onSuccess = () => {},
  onBtnClick = () => {},
  handler,
  render,
  signModalProps,
}) => {
  const [value, setValue] = useItemLiked(id, initialValue)
  const [count, setCount] = useItemLikedCount(id, initialCount)

  const { data: userInfo } = useMyInfo()

  const onClick = useCallback(
    once(async (e: MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()

      const target = !value
      // provide convenince for e.g. tracking events
      onBtnClick(target)

      if (!id) return
      if (!userInfo) {
        openSignModal(signModalProps, { fromPath: 'like' })
        return
      }

      setValue(target)
      setCount(count + (target ? 1 : -1))

      try {
        await handler(id, target)
        onSuccess(target)
      } catch (err: any) {
        setValue(value)
        setCount(count)

        const errCode = getErrorCode(err)
        if (errCode === 40000013) {
          toast.error(
            <>
              <Text i18nKey="common:block.denied-message" />
            </>,
          )
        } else {
          toast.error(err.message)
        }

        console.error(err)
      }
    }),
    [userInfo, value, count, signModalProps],
  )

  return render({
    value,
    count,
    onClick,
  })
}

export default BaseLikeButton
